import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TodoApi } from "../api";
import {
  TodoData,
  CategoryData,
  CreateTodoInput,
  Context as TodoContext,
} from "../../../types";

const dataContext = createContext<{
  todos: TodoData[];
  categories: CategoryData[];
  loading: boolean;
  createTodo: (input: Omit<CreateTodoInput, "id">) => Promise<void>;
  updateTodo: (todo: TodoData, updates: Partial<TodoData>) => Promise<void>;
  deleteTodo: (todo: TodoData) => Promise<void>;
  createCategory: (name: string, context?: TodoContext) => Promise<void>;
}>({
  todos: [],
  categories: [],
  loading: true,
  createTodo: async () => {},
  updateTodo: async () => {},
  deleteTodo: async () => {},
  createCategory: async () => {},
});

const nowISO = () => new Date().toISOString();

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  const [todos, setTodos] = useState<TodoData[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await TodoApi.getAll();
    if (result.success && result.data) {
      setTodos(result.data.todos);
      setCategories(result.data.categories);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistic: the client generates the id and renders immediately, then
  // reconciles with whatever the server actually stored (server sets
  // createdAt/updatedAt, which is the source of truth for the sort key).
  const createTodo = async (input: Omit<CreateTodoInput, "id">) => {
    const id = crypto.randomUUID();
    const optimistic: TodoData = {
      type: "todo",
      id,
      userId: "",
      title: input.title,
      notes: input.notes,
      context: input.context,
      categoryId: input.categoryId,
      priority: input.priority,
      complexity: input.complexity,
      done: false,
      order: -Date.now(),
      timesScheduled: input.scheduledDate ? 1 : 0,
      scheduledDate: input.scheduledDate,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    };
    setTodos((t) => [...t, optimistic]);
    const result = await TodoApi.createTodo({ ...input, id });
    if (result.success && result.data) {
      setTodos((t) => t.map((existing) => (existing.id === id ? result.data : existing)));
    }
  };

  const updateTodo = async (todo: TodoData, updates: Partial<TodoData>) => {
    setTodos((t) =>
      t.map((existing) =>
        existing.id === todo.id ? { ...existing, ...updates } : existing,
      ),
    );
    await TodoApi.updateTodo({
      id: todo.id,
      createdAt: todo.createdAt,
      ...updates,
    });
  };

  const deleteTodo = async (todo: TodoData) => {
    setTodos((t) => t.filter((existing) => existing.id !== todo.id));
    await TodoApi.deleteTodo(todo.id, todo.createdAt);
  };

  const createCategory = async (name: string, context?: TodoContext) => {
    const id = crypto.randomUUID();
    const optimistic: CategoryData = {
      type: "category",
      id,
      userId: "",
      name,
      context,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    };
    setCategories((c) => [...c, optimistic]);
    await TodoApi.createCategory({ id, name, context });
  };

  return (
    <dataContext.Provider
      value={{
        todos,
        categories,
        loading,
        createTodo,
        updateTodo,
        deleteTodo,
        createCategory,
      }}
    >
      {children}
    </dataContext.Provider>
  );
};

export const useData = () => useContext(dataContext);
